import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  CreditCard,
  DollarSign,
  CheckCircle2,
  Clock,
  AlertCircle,
  Banknote,
  Building,
  Receipt,
  Lock,
} from "lucide-react";
import Modal from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import apiClient from "@/api/apiClient";
import { API_ENDPOINTS } from "@/api/header";

const PAYMENT_STATUS_OPTIONS = [
  { value: "completed", label: "Completed", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { value: "pending", label: "Pending", color: "bg-amber-50 text-amber-700 border-amber-200" },
  { value: "partially-paid", label: "Partially Paid", color: "bg-blue-50 text-blue-700 border-blue-200" },
  { value: "on-hold", label: "On Hold", color: "bg-purple-50 text-purple-700 border-purple-200" },
  { value: "failed", label: "Failed", color: "bg-rose-50 text-rose-700 border-rose-200" },
  { value: "cancelled", label: "Cancelled", color: "bg-slate-50 text-slate-700 border-slate-200" },
  { value: "refunded", label: "Refunded", color: "bg-gray-50 text-gray-700 border-gray-200" },
  { value: "partially-refunded", label: "Partially Refunded", color: "bg-orange-50 text-orange-700 border-orange-200" },
];

const CARD_TYPES = [
  { value: "visa", label: "Visa" },
  { value: "mastercard", label: "Mastercard" },
  { value: "amex", label: "American Express" },
  { value: "discover", label: "Discover" },
  { value: "other", label: "Other" },
];

const getNowDateTimeLocal = () => {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 16);
};

const getMethodIcon = (val = "") => {
  if (!val) return DollarSign;
  const nameOrCode =
    typeof val === "string"
      ? val
      : typeof val === "object"
        ? (val.name || val.code || val.title || val.slug || "")
        : String(val);

  const lower = String(nameOrCode || "").toLowerCase();
  if (lower.includes("cash")) return Banknote;
  if (lower.includes("card") || lower.includes("credit") || lower.includes("debit"))
    return CreditCard;
  if (lower.includes("bank") || lower.includes("transfer") || lower.includes("wire"))
    return Building;
  if (lower.includes("cheque") || lower.includes("check")) return Receipt;
  return DollarSign;
};

const RecordRepairPaymentInner = ({
  show,
  onHide,
  repairJob,
  currencySymbol = "$",
  onSuccess,
  hasPermission: propHasPermission,
}) => {
  const queryClient = useQueryClient();
  const currentStore = useSelector((state) => state.current_store);
  const userPermissions = useSelector((state) => state.userpermission_data);

  const hasPermission = useMemo(() => {
    if (typeof propHasPermission === "function") return propHasPermission;
    return (perm) => Boolean(userPermissions && userPermissions.includes(perm));
  }, [propHasPermission, userPermissions]);

  const canAddPayment = hasPermission("repair job add_payment");
  const canViewPayments = hasPermission("repair job view_payments");

  const storeId = repairJob?.store_id || currentStore?.id;

  const [selectedTab, setSelectedTab] = useState("record");
  const [validationErrors, setValidationErrors] = useState({});

  const initialTotalCost = useMemo(() => {
    return parseFloat(repairJob?.total_cost ?? repairJob?.estimated_cost ?? 0);
  }, [repairJob]);

  // ─── Fetch available payment methods with SCOPE = "sales" ────────────────
  const {
    data: availablePaymentMethods = [],
    isLoading: isPaymentMethodsLoading,
  } = useQuery({
    queryKey: ["payment-methods-available", "sales", storeId],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.PAYMENT_METHODS.AVAILABLE, {
        params: {
          scope: "sales",
          store_id: storeId || undefined,
        },
      });
      return res.data?.data ?? res.data ?? [];
    },
    enabled: Boolean(show),
    staleTime: 5 * 60 * 1000,
  });

  // Fallback payment methods if backend has none
  const paymentMethodsList = useMemo(() => {
    if (availablePaymentMethods.length > 0) {
      return availablePaymentMethods.map((pm) => ({
        id: String(pm.id || pm.slug || pm.code || pm),
        code: pm.code || pm.slug || pm.id || "",
        name: pm.name || pm.title || pm.label || String(pm),
      }));
    }
    return [
      { id: "cash", code: "cash", name: "Cash" },
      { id: "credit_card", code: "card", name: "Credit Card" },
      { id: "debit_card", code: "card", name: "Debit Card" },
      { id: "bank_transfer", code: "bank_transfer", name: "Bank Transfer" },
      { id: "cheque", code: "cheque", name: "Cheque" },
      { id: "other", code: "other", name: "Other" },
    ];
  }, [availablePaymentMethods]);

  // ─── Fetch existing payments for this repair job (only if repair job view_payments) ───
  const {
    data: paymentsData = [],
    isLoading: isLoadingPayments,
    refetch: refetchPayments,
  } = useQuery({
    queryKey: ["repair-job-payments", repairJob?.id],
    queryFn: async () => {
      if (!repairJob?.id) return [];
      try {
        const res = await apiClient.get(
          API_ENDPOINTS.REPAIRS.PAYMENTS(repairJob.id),
        );
        return res.data?.data ?? res.data ?? [];
      } catch {
        return repairJob.payments || [];
      }
    },
    enabled: Boolean(show && repairJob?.id && canViewPayments),
    staleTime: 30 * 1000,
  });

  // ─── Fetch fresh repair details (with updated balances & statuses) ───
  const { data: freshRepairJob, refetch: refetchJob } = useQuery({
    queryKey: ["repair-details", repairJob?.id],
    queryFn: async () => {
      if (!repairJob?.id) return null;
      try {
        const res = await apiClient.get(
          API_ENDPOINTS.REPAIRS.REPAIR_DETAILS(repairJob.id),
        );
        return res.data?.data ?? res.data;
      } catch {
        return null;
      }
    },
    enabled: Boolean(show && repairJob?.id),
    staleTime: 0,
  });

  const currentJob = freshRepairJob || repairJob;

  // ─── Financial Calculations ───────────────────────────────────────────────
  const totalCost = parseFloat(
    currentJob?.total_cost ?? currentJob?.estimated_cost ?? initialTotalCost,
  );

  const totalPaid = (() => {
    if (!canViewPayments) return 0;
    if (Array.isArray(paymentsData) && paymentsData.length > 0) {
      return paymentsData
        .filter((p) => {
          const status = (p.payment_status || "completed").toLowerCase();
          return (
            status === "completed" ||
            status === "paid" ||
            status === "partially-paid"
          );
        })
        .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    }
    if (currentJob?.paid_amount != null) {
      return parseFloat(currentJob.paid_amount);
    }
    return 0;
  })();

  const remainingBalance = (() => {
    if (
      currentJob?.balance_due != null &&
      (!paymentsData || paymentsData.length === 0)
    ) {
      return Math.max(0, parseFloat(currentJob.balance_due));
    }
    return Math.max(0, parseFloat((totalCost - totalPaid).toFixed(2)));
  })();

  const isPaidInFull = (() => {
    const pStatus = (currentJob?.payment_status || "").toLowerCase();
    if (pStatus === "completed" || pStatus === "paid") return true;
    return remainingBalance <= 0 && (totalPaid > 0 || totalCost > 0);
  })();

  const activeTab =
    (!canAddPayment || isPaidInFull) && canViewPayments
      ? "history"
      : selectedTab;

  const [formData, setFormData] = useState(() => {
    const initBalance =
      repairJob?.balance_due != null
        ? parseFloat(repairJob.balance_due)
        : Math.max(0, initialTotalCost - parseFloat(repairJob?.paid_amount || 0));

    const initialAmount =
      initBalance > 0
        ? initBalance.toFixed(2)
        : initialTotalCost > 0
          ? initialTotalCost.toFixed(2)
          : "";

    return {
      amount: initialAmount,
      payment_method: "",
      payment_status: "completed",
      payment_date: getNowDateTimeLocal(),
      transaction_id: "",
      card_type: "visa",
      card_last_four: "",
      authorization_code: "",
      notes: "",
    };
  });

  const activeMethod = useMemo(() => {
    if (formData.payment_method) {
      return (
        paymentMethodsList.find((m) => m.id === formData.payment_method) || {
          id: formData.payment_method,
          code: formData.payment_method,
          name: formData.payment_method,
        }
      );
    }
    return paymentMethodsList[0] || { id: "cash", code: "cash", name: "Cash" };
  }, [formData.payment_method, paymentMethodsList]);

  // Determine if selected payment method is a Card
  const isCard = useMemo(() => {
    const text = `${activeMethod.name} ${activeMethod.code} ${activeMethod.id}`.toLowerCase();
    return text.includes("card") || text.includes("credit") || text.includes("debit");
  }, [activeMethod]);

  // ─── Mutation: Record Payment ──────────────────────────────────────────────
  const recordPaymentMutation = useMutation({
    mutationFn: async (payload) => {
      const res = await apiClient.post(
        API_ENDPOINTS.REPAIRS.PAYMENTS(repairJob.id),
        payload,
      );
      return res.data;
    },
    onSuccess: async (data) => {
      const recordedAmount = parseFloat(formData.amount || 0);
      toast.success(
        data?.message ||
        `Payment of ${currencySymbol}${recordedAmount.toFixed(2)} recorded successfully!`,
      );
      queryClient.invalidateQueries({ queryKey: ["repair-jobs"] });
      queryClient.invalidateQueries({
        queryKey: ["repair-job-payments", repairJob.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["repair-details", repairJob.id],
      });

      const updatedPaymentsResult = await refetchPayments();
      await refetchJob();

      if (onSuccess) onSuccess(data);

      // Do NOT close the modal - keep it open!
      // Calculate remaining balance to update form for subsequent partial payments
      const newPaymentsList = updatedPaymentsResult?.data || [];
      const updatedTotalPaid = Array.isArray(newPaymentsList) && newPaymentsList.length > 0
        ? newPaymentsList
          .filter((p) => {
            const status = (p.payment_status || "completed").toLowerCase();
            return status === "completed" || status === "paid" || status === "partially-paid";
          })
          .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0)
        : totalPaid + recordedAmount;

      const newRemaining = Math.max(0, parseFloat((totalCost - updatedTotalPaid).toFixed(2)));

      if (newRemaining <= 0) {
        setFormData((prev) => ({
          ...prev,
          amount: "",
          transaction_id: "",
          card_last_four: "",
          authorization_code: "",
          notes: "",
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          amount: newRemaining.toFixed(2),
          transaction_id: "",
          card_last_four: "",
          authorization_code: "",
          notes: "",
        }));
      }
    },
    onError: (err) => {
      const resp = err.response?.data;
      if (resp?.errors) {
        setValidationErrors(resp.errors);
        const firstErrorKey = Object.keys(resp.errors)[0];
        const errorMsg = Array.isArray(resp.errors[firstErrorKey])
          ? resp.errors[firstErrorKey][0]
          : resp.errors[firstErrorKey];
        toast.error(errorMsg || "Validation failed");
      } else {
        toast.error(
          resp?.message || err.message || "Failed to record payment. Please try again.",
        );
      }
    },
  });

  const handleQuickAmount = (type) => {
    if (type === "full") {
      setFormData((prev) => ({
        ...prev,
        amount: remainingBalance > 0 ? remainingBalance.toFixed(2) : totalCost.toFixed(2),
      }));
    } else if (type === "half") {
      const base = remainingBalance > 0 ? remainingBalance : totalCost;
      setFormData((prev) => ({
        ...prev,
        amount: (base / 2).toFixed(2),
      }));
    } else if (type === "clear") {
      setFormData((prev) => ({ ...prev, amount: "" }));
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();

    if (!canAddPayment) {
      toast.error("You do not have permission to record payments for repair jobs");
      return;
    }

    if (isPaidInFull) {
      toast.info("This repair job is already paid in full.");
      return;
    }

    const errors = {};

    const parsedAmount = parseFloat(formData.amount);
    if (!formData.amount || isNaN(parsedAmount) || parsedAmount < 0.01) {
      errors.amount = "Amount must be at least 0.01";
    }

    const resolvedMethod = formData.payment_method || activeMethod.id;
    if (!resolvedMethod) {
      errors.payment_method = "Please select a payment method";
    }

    if (isCard) {
      if (formData.card_last_four && formData.card_last_four.length !== 4) {
        errors.card_last_four = "Last 4 digits must be exactly 4 characters";
      }
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      toast.error(Object.values(errors)[0]);
      return;
    }

    setValidationErrors({});

    const payload = {
      amount: parsedAmount,
      payment_method: resolvedMethod,
      payment_status: formData.payment_status || "completed",
      payment_date: formData.payment_date
        ? new Date(formData.payment_date).toISOString()
        : new Date().toISOString(),
      notes: formData.notes?.trim() || null,
    };

    if (formData.transaction_id?.trim()) {
      payload.transaction_id = formData.transaction_id.trim();
    }

    if (isCard) {
      if (formData.card_type) payload.card_type = formData.card_type;
      if (formData.card_last_four?.trim()) {
        payload.card_last_four = formData.card_last_four.trim();
      }
      if (formData.authorization_code?.trim()) {
        payload.authorization_code = formData.authorization_code.trim();
      }
    }

    recordPaymentMutation.mutate(payload);
  };

  const customerName =
    currentJob.customer?.customer_type !== "individual" && currentJob.customer?.company
      ? currentJob.customer.company
      : currentJob.customer?.full_name || "Walk-in Customer";

  return (
    <Modal
      open={show}
      onOpenChange={(v) => !v && onHide()}
      title={
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <span>{canAddPayment && !isPaidInFull ? "Record Payment" : "Payment Details"}</span>
            <Badge variant="outline" className="text-xs font-mono font-medium">
              Job #{currentJob.repair_number || currentJob.id?.slice(0, 8)}
            </Badge>
          </div>
          <span className="text-xs text-muted-foreground font-normal">
            Customer: <strong className="text-foreground">{customerName}</strong>
            {currentJob.item_description ? ` • ${currentJob.item_description}` : ""}
          </span>
        </div>
      }
      icon={CreditCard}
      maxWidth="max-w-2xl"
      footer={
        <div className="flex items-center justify-between w-full">
          <div className="text-xs text-muted-foreground hidden sm:block">
            Scope: <span className="font-semibold text-foreground">Sales</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onHide}
              disabled={recordPaymentMutation.isPending}
            >
              {canAddPayment && !isPaidInFull ? "Cancel" : "Close"}
            </Button>
            {isPaidInFull ? (
              <Button
                type="button"
                disabled
                className="min-w-[140px] bg-emerald-600/60 text-white cursor-not-allowed gap-2 shadow-sm"
              >
                <CheckCircle2 size={16} /> Paid in Full
              </Button>
            ) : canAddPayment ? (
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={recordPaymentMutation.isPending || isPaymentMethodsLoading}
                className="min-w-[140px] bg-emerald-600 hover:bg-emerald-700 text-white gap-2 shadow-sm"
              >
                {recordPaymentMutation.isPending ? (
                  <>
                    <Spinner size={16} /> Processing...
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={16} /> Record Payment
                  </>
                )}
              </Button>
            ) : null}
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {/* ── Balance Overview Banner ── */}
        <Card className="bg-gradient-to-br from-slate-50 to-muted/40 border border-border/80 shadow-none overflow-hidden">
          <CardContent className="p-4">
            <div
              className={`grid ${canViewPayments ? "grid-cols-3 divide-x" : "grid-cols-1"
                } gap-4 text-center divide-border/60`}
            >
              <div className="space-y-0.5">
                <span className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground block">
                  Total Cost
                </span>
                <span className="text-lg font-bold text-foreground">
                  {currencySymbol}
                  {totalCost.toFixed(2)}
                </span>
              </div>
              {canViewPayments && (
                <>
                  <div className="space-y-0.5">
                    <span className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground block">
                      Total Paid
                    </span>
                    <span className="text-lg font-bold text-emerald-600">
                      {currencySymbol}
                      {totalPaid.toFixed(2)}
                    </span>
                  </div>
                  <div className="space-y-0.5">
                    <span className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground block">
                      Balance Due
                    </span>
                    <span
                      className={`text-lg font-bold ${remainingBalance > 0 ? "text-amber-600" : "text-emerald-600"
                        }`}
                    >
                      {currencySymbol}
                      {remainingBalance.toFixed(2)}
                    </span>
                  </div>
                </>
              )}
            </div>

            {canViewPayments && remainingBalance === 0 && totalCost > 0 && (
              <div className="mt-3 py-1 px-2.5 bg-emerald-100/60 text-emerald-800 text-xs font-medium rounded-md flex items-center justify-center gap-1.5">
                <CheckCircle2 size={14} />
                This repair job has already been fully paid.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Permission warning if user cannot add payment */}
        {!canAddPayment && (
          <div className="p-3 bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-md flex items-center gap-2">
            <Lock size={14} className="shrink-0 text-amber-600" />
            <span>
              You only have view access. Permission <code>repair job add_payment</code> is required to record payments.
            </span>
          </div>
        )}

        {isPaidInFull && (
          <div className="flex items-center gap-2 p-3 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 text-xs">
            <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
            <span>
              This repair job is <strong>paid in full</strong>. No further payment is required.
            </span>
          </div>
        )}

        {/* ── Tabs: Record Payment vs History ── */}
        {canViewPayments ? (
          <Tabs value={activeTab} onValueChange={setSelectedTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="record" disabled={!canAddPayment || isPaidInFull} className="gap-2">
                <DollarSign size={15} /> Payment Details
              </TabsTrigger>
              <TabsTrigger value="history" className="gap-2">
                <Clock size={15} /> Payment History
                {paymentsData.length > 0 && (
                  <Badge
                    variant="secondary"
                    className="ml-1 h-5 px-1.5 text-[11px] font-semibold"
                  >
                    {paymentsData.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            {/* ──────── TAB 1: RECORD FORM ──────── */}
            <TabsContent value="record" className="space-y-4 pt-3 mt-0">
              {/* Amount & Quick Buttons */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="payment-amount" className="font-semibold text-sm">
                    Amount <span className="text-destructive">*</span>
                  </Label>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] text-muted-foreground mr-1">
                      Quick Fill:
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-6 text-xs px-2 py-0"
                      onClick={() => handleQuickAmount("full")}
                      disabled={!canAddPayment || isPaidInFull}
                    >
                      Full Balance
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-6 text-xs px-2 py-0"
                      onClick={() => handleQuickAmount("half")}
                      disabled={!canAddPayment || isPaidInFull}
                    >
                      50%
                    </Button>
                  </div>
                </div>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-semibold text-base">
                    {currencySymbol}
                  </span>
                  <Input
                    id="payment-amount"
                    type="number"
                    step="0.01"
                    min="0.01"
                    placeholder="0.00"
                    disabled={!canAddPayment || isPaidInFull}
                    className="pl-8 text-lg font-bold"
                    value={formData.amount}
                    onChange={(e) =>
                      setFormData({ ...formData, amount: e.target.value })
                    }
                  />
                </div>
                {validationErrors.amount && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle size={12} /> {validationErrors.amount}
                  </p>
                )}
              </div>

              {/* Payment Method & Status */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="payment-method" className="font-semibold text-sm">
                    Payment Method <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    disabled={!canAddPayment || isPaidInFull}
                    value={formData.payment_method || activeMethod.id}
                    onValueChange={(val) =>
                      setFormData({ ...formData, payment_method: val })
                    }
                  >
                    <SelectTrigger id="payment-method">
                      <SelectValue placeholder="Select method" />
                    </SelectTrigger>
                    <SelectContent>
                      {paymentMethodsList.map((pm) => {
                        const Icon = getMethodIcon(pm.name || pm.code);
                        return (
                          <SelectItem key={pm.id} value={pm.id}>
                            <div className="flex items-center gap-2">
                              <Icon size={14} className="text-muted-foreground" />
                              <span>{pm.name}</span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  {validationErrors.payment_method && (
                    <p className="text-xs text-destructive flex items-center gap-1">
                      <AlertCircle size={12} /> {validationErrors.payment_method}
                    </p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="payment-status" className="font-semibold text-sm">
                    Payment Status
                  </Label>
                  <Select
                    disabled={!canAddPayment || isPaidInFull}
                    value={formData.payment_status}
                    onValueChange={(val) =>
                      setFormData({ ...formData, payment_status: val })
                    }
                  >
                    <SelectTrigger id="payment-status">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_STATUS_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          <div className="flex items-center gap-2">
                            <span
                              className={`inline-block w-2 h-2 rounded-full ${opt.value === "completed"
                                ? "bg-emerald-500"
                                : opt.value === "pending"
                                  ? "bg-amber-500"
                                  : "bg-blue-500"
                                }`}
                            />
                            <span>{opt.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Payment Date & Transaction ID */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="payment-date" className="font-semibold text-sm">
                    Payment Date & Time
                  </Label>
                  <Input
                    id="payment-date"
                    type="datetime-local"
                    disabled={!canAddPayment || isPaidInFull}
                    value={formData.payment_date}
                    onChange={(e) =>
                      setFormData({ ...formData, payment_date: e.target.value })
                    }
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="transaction-id" className="font-semibold text-sm">
                    Transaction / Reference ID
                  </Label>
                  <Input
                    id="transaction-id"
                    placeholder="e.g. TXN-948210"
                    maxLength={255}
                    disabled={!canAddPayment || isPaidInFull}
                    value={formData.transaction_id}
                    onChange={(e) =>
                      setFormData({ ...formData, transaction_id: e.target.value })
                    }
                  />
                </div>
              </div>

              {/* Card Specific Fields (Conditional) */}
              {isCard && (
                <div className="p-3.5 rounded-lg border border-dashed border-primary/30 bg-primary/5 space-y-3">
                  <div className="flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wide">
                    <CreditCard size={14} /> Card Details
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Card Type</Label>
                      <Select
                        disabled={!canAddPayment || isPaidInFull}
                        value={formData.card_type}
                        onValueChange={(val) =>
                          setFormData({ ...formData, card_type: val })
                        }
                      >
                        <SelectTrigger className="h-9 text-xs">
                          <SelectValue placeholder="Card Type" />
                        </SelectTrigger>
                        <SelectContent>
                          {CARD_TYPES.map((ct) => (
                            <SelectItem key={ct.value} value={ct.value}>
                              {ct.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs">Last 4 Digits</Label>
                      <Input
                        placeholder="1234"
                        maxLength={4}
                        disabled={!canAddPayment || isPaidInFull}
                        className="h-9 text-xs font-mono"
                        value={formData.card_last_four}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            card_last_four: e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 4),
                          })
                        }
                      />
                      {validationErrors.card_last_four && (
                        <p className="text-[11px] text-destructive">
                          {validationErrors.card_last_four}
                        </p>
                      )}
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs">Authorization Code</Label>
                      <Input
                        placeholder="e.g. AUTH-5542"
                        maxLength={255}
                        disabled={!canAddPayment || isPaidInFull}
                        className="h-9 text-xs"
                        value={formData.authorization_code}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            authorization_code: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Notes */}
              <div className="space-y-1.5">
                <Label htmlFor="payment-notes" className="font-semibold text-sm">
                  Notes
                </Label>
                <Textarea
                  id="payment-notes"
                  placeholder="Optional notes or remarks regarding this payment..."
                  rows={2}
                  disabled={!canAddPayment || isPaidInFull}
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                />
              </div>
            </TabsContent>

            {/* ──────── TAB 2: HISTORY ──────── */}
            <TabsContent value="history" className="space-y-3 pt-3 mt-0">
              {isLoadingPayments ? (
                <div className="flex flex-col items-center justify-center py-8 text-muted-foreground gap-2">
                  <Spinner size={24} />
                  <span className="text-xs">Loading payments...</span>
                </div>
              ) : paymentsData.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground border rounded-lg border-dashed">
                  <Receipt className="mx-auto h-8 w-8 text-muted-foreground/40 mb-2" />
                  <p className="text-sm font-medium">No payments recorded yet</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Payments recorded for this repair job will appear here.
                  </p>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden divide-y divide-border">
                  {paymentsData.map((payment, idx) => {
                    const rawMethod = payment.payment_method;
                    const methodIdOrCode =
                      typeof rawMethod === "object" && rawMethod !== null
                        ? String(rawMethod.id || rawMethod.code || rawMethod.name || "")
                        : String(rawMethod || "");

                    const methodName =
                      typeof rawMethod === "object" && rawMethod !== null
                        ? rawMethod.name || rawMethod.title || rawMethod.label || ""
                        : "";

                    const methodObj = paymentMethodsList.find(
                      (m) =>
                        m.id === methodIdOrCode ||
                        m.code === methodIdOrCode ||
                        (methodName && (m.name === methodName || m.id === methodName)),
                    );

                    const displayName =
                      methodObj?.name || methodName || methodIdOrCode || `Payment #${idx + 1}`;

                    const Icon = getMethodIcon(displayName);
                    const statusConfig =
                      PAYMENT_STATUS_OPTIONS.find(
                        (o) => o.value === payment.payment_status,
                      ) || {
                        label: payment.payment_status || "Completed",
                        color: "bg-slate-50 text-slate-700",
                      };

                    return (
                      <div
                        key={payment.id || idx}
                        className="p-3 flex items-center justify-between gap-3 text-sm hover:bg-muted/30 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                            <Icon size={16} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">
                                {displayName}
                              </span>
                              <span
                                className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${statusConfig.color}`}
                              >
                                {statusConfig.label}
                              </span>
                            </div>
                            <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                              <span>
                                {payment.payment_date
                                  ? new Date(payment.payment_date).toLocaleString(
                                    undefined,
                                    {
                                      dateStyle: "medium",
                                      timeStyle: "short",
                                    },
                                  )
                                  : "N/A"}
                              </span>
                              {payment.transaction_id && (
                                <span>• Ref: {payment.transaction_id}</span>
                              )}
                              {payment.card_last_four && (
                                <span>• Card ending {payment.card_last_four}</span>
                              )}
                            </div>
                            {payment.notes && (
                              <p className="text-xs text-muted-foreground italic mt-0.5">
                                {payment.notes}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <span className="font-bold text-base text-foreground">
                            {currencySymbol}
                            {parseFloat(payment.amount || 0).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          </Tabs>
        ) : (
          /* When user only has repair job add_payment and not view_payments */
          <div className="space-y-4 pt-1">
            {/* Amount & Quick Buttons */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="payment-amount" className="font-semibold text-sm">
                  Amount <span className="text-destructive">*</span>
                </Label>
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] text-muted-foreground mr-1">
                    Quick Fill:
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-6 text-xs px-2 py-0"
                    onClick={() => handleQuickAmount("full")}
                  >
                    Full Balance
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-6 text-xs px-2 py-0"
                    onClick={() => handleQuickAmount("half")}
                  >
                    50%
                  </Button>
                </div>
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-semibold text-base">
                  {currencySymbol}
                </span>
                <Input
                  id="payment-amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  placeholder="0.00"
                  className="pl-8 text-lg font-bold"
                  value={formData.amount}
                  onChange={(e) =>
                    setFormData({ ...formData, amount: e.target.value })
                  }
                />
              </div>
              {validationErrors.amount && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle size={12} /> {validationErrors.amount}
                </p>
              )}
            </div>

            {/* Payment Method & Status */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="payment-method" className="font-semibold text-sm">
                  Payment Method <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={formData.payment_method || activeMethod.id}
                  onValueChange={(val) =>
                    setFormData({ ...formData, payment_method: val })
                  }
                >
                  <SelectTrigger id="payment-method">
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentMethodsList.map((pm) => {
                      const Icon = getMethodIcon(pm.name || pm.code);
                      return (
                        <SelectItem key={pm.id} value={pm.id}>
                          <div className="flex items-center gap-2">
                            <Icon size={14} className="text-muted-foreground" />
                            <span>{pm.name}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {validationErrors.payment_method && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle size={12} /> {validationErrors.payment_method}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="payment-status" className="font-semibold text-sm">
                  Payment Status
                </Label>
                <Select
                  value={formData.payment_status}
                  onValueChange={(val) =>
                    setFormData({ ...formData, payment_status: val })
                  }
                >
                  <SelectTrigger id="payment-status">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_STATUS_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-block w-2 h-2 rounded-full ${opt.value === "completed"
                              ? "bg-emerald-500"
                              : opt.value === "pending"
                                ? "bg-amber-500"
                                : "bg-blue-500"
                              }`}
                          />
                          <span>{opt.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Payment Date & Transaction ID */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="payment-date" className="font-semibold text-sm">
                  Payment Date & Time
                </Label>
                <Input
                  id="payment-date"
                  type="datetime-local"
                  value={formData.payment_date}
                  onChange={(e) =>
                    setFormData({ ...formData, payment_date: e.target.value })
                  }
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="transaction-id" className="font-semibold text-sm">
                  Transaction / Reference ID
                </Label>
                <Input
                  id="transaction-id"
                  placeholder="e.g. TXN-948210"
                  maxLength={255}
                  value={formData.transaction_id}
                  onChange={(e) =>
                    setFormData({ ...formData, transaction_id: e.target.value })
                  }
                />
              </div>
            </div>

            {/* Card Specific Fields */}
            {isCard && (
              <div className="p-3.5 rounded-lg border border-dashed border-primary/30 bg-primary/5 space-y-3">
                <div className="flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wide">
                  <CreditCard size={14} /> Card Details
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Card Type</Label>
                    <Select
                      value={formData.card_type}
                      onValueChange={(val) =>
                        setFormData({ ...formData, card_type: val })
                      }
                    >
                      <SelectTrigger className="h-9 text-xs">
                        <SelectValue placeholder="Card Type" />
                      </SelectTrigger>
                      <SelectContent>
                        {CARD_TYPES.map((ct) => (
                          <SelectItem key={ct.value} value={ct.value}>
                            {ct.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">Last 4 Digits</Label>
                    <Input
                      placeholder="1234"
                      maxLength={4}
                      className="h-9 text-xs font-mono"
                      value={formData.card_last_four}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          card_last_four: e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 4),
                        })
                      }
                    />
                    {validationErrors.card_last_four && (
                      <p className="text-[11px] text-destructive">
                        {validationErrors.card_last_four}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">Authorization Code</Label>
                    <Input
                      placeholder="e.g. AUTH-5542"
                      maxLength={255}
                      className="h-9 text-xs"
                      value={formData.authorization_code}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          authorization_code: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Notes */}
            <div className="space-y-1.5">
              <Label htmlFor="payment-notes" className="font-semibold text-sm">
                Notes
              </Label>
              <Textarea
                id="payment-notes"
                placeholder="Optional notes or remarks regarding this payment..."
                rows={2}
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
              />
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

RecordRepairPaymentInner.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  repairJob: PropTypes.object.isRequired,
  currencySymbol: PropTypes.string,
  onSuccess: PropTypes.func,
  hasPermission: PropTypes.func,
};

const RecordRepairPaymentModal = (props) => {
  if (!props.show || !props.repairJob) return null;
  return <RecordRepairPaymentInner key={props.repairJob?.id} {...props} />;
};

RecordRepairPaymentModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  repairJob: PropTypes.object,
  currencySymbol: PropTypes.string,
  onSuccess: PropTypes.func,
  hasPermission: PropTypes.func,
};

export default RecordRepairPaymentModal;
